/**
 * Swap Meal Dialog Component
 *
 * Portal-based dialog for complete meal replacement with AI generation.
 * Features textarea with 140 char limit, character counter, Mode button group (Fast/Quality),
 * dynamic ETA label, loading dialog with progress bar, and unsaved changes confirmation.
 *
 * @module components/meal-plans/SwapMealDialog
 */

import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from '@/hooks/useTheme';
import { Button } from '@/components/ui/Button';
import { TextArea } from '@/components/ui/TextArea';
import { ButtonGroup } from '@/components/ui/ButtonGroup';
import { LoadingDialog } from '@/components/ui/LoadingDialog';
import { CloseButton } from '@/components/ui/CloseButton';
import { useMealPlanStore } from '@/stores/mealPlanStore';
import type { MealPlanFrontend, SwapMealFormData } from '@/types/mealPlan';

/**
 * Props for SwapMealDialog component
 */
export interface SwapMealDialogProps {
  /** Whether the dialog is open */
  isOpen: boolean;
  /** Callback to close the dialog */
  onClose: () => void;
  /** The meal to swap */
  meal: MealPlanFrontend['mealPlanData']['meals'][0];
  /** ID of the meal plan containing this meal */
  mealPlanId: string;
  /** Callback when meal is successfully swapped */
  onSuccess?: () => void;
  /** Callback when swap fails */
  onError?: (error: Error) => void;
}

/**
 * Swap Meal Dialog Component
 *
 * Allows dietitians to completely replace a meal with an entirely new one
 * based on special instructions.
 */
export const SwapMealDialog: React.FC<SwapMealDialogProps> = ({
  isOpen,
  onClose,
  meal,
  mealPlanId,
  onSuccess,
  onError,
}) => {
  const { isDark } = useTheme();
  const [specialInstructions, setSpecialInstructions] = useState('');
  const [mode, setMode] = useState<'fast' | 'quality'>('fast');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showConfirmClose, setShowConfirmClose] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const swapMeal = useMealPlanStore((state) => state.swapMeal);
  const operations = useMealPlanStore((state) => state.operations);
  const errors = useMealPlanStore((state) => state.errors);

  const operationKey = `${mealPlanId}-${meal.id}`;
  const isSwapping = operations.swapping[operationKey] || false;
  const error = errors[operationKey];

  // Character limits (must match backend MealSwapRequest.guidance)
  const MIN_INSTRUCTIONS_LENGTH = 10;
  const MAX_INSTRUCTIONS_LENGTH = 560;

  // Dynamic ETA based on mode
  const estimatedTimeSeconds = mode === 'fast' ? 30 : 90;

  // Reset form and focus textarea when dialog opens
  useEffect(() => {
    if (isOpen) {
      setSpecialInstructions('');
      setMode('fast');
      setHasUnsavedChanges(false);
      setShowConfirmClose(false);
      setValidationError(null);

      // Focus textarea after a short delay to ensure DOM is ready
      setTimeout(() => {
        textareaRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  // Track unsaved changes
  useEffect(() => {
    setHasUnsavedChanges(specialInstructions.trim().length > 0);
  }, [specialInstructions]);

  // Handle escape key press
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen && !isSwapping) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, isSwapping, hasUnsavedChanges]);

  // Validate special instructions
  const validateInstructions = (value: string): string | null => {
    if (value.trim().length === 0) {
      return 'Special instructions are required';
    }
    if (value.trim().length < MIN_INSTRUCTIONS_LENGTH) {
      return `Instructions must be at least ${MIN_INSTRUCTIONS_LENGTH} characters`;
    }
    if (value.trim().length > MAX_INSTRUCTIONS_LENGTH) {
      return `Instructions must be ${MAX_INSTRUCTIONS_LENGTH} characters or less`;
    }
    return null;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const error = validateInstructions(specialInstructions);
    if (error) {
      setValidationError(error);
      return;
    }

    try {
      const formData: SwapMealFormData = {
        specialInstructions: specialInstructions.trim(),
        skipEvaluation: mode === 'fast', // Fast → true, Quality → false
      };

      await swapMeal(mealPlanId, meal.id, formData);

      // Success
      setSpecialInstructions('');
      setHasUnsavedChanges(false);
      onSuccess?.();
      onClose();
    } catch (err) {
      onError?.(err as Error);
    }
  };

  // Handle close with unsaved changes confirmation
  const handleClose = () => {
    if (hasUnsavedChanges && !showConfirmClose) {
      setShowConfirmClose(true);
    } else {
      setSpecialInstructions('');
      setMode('fast');
      setHasUnsavedChanges(false);
      setShowConfirmClose(false);
      setValidationError(null);
      onClose();
    }
  };

  // Handle discard changes
  const handleDiscardChanges = () => {
    setSpecialInstructions('');
    setMode('fast');
    setHasUnsavedChanges(false);
    setShowConfirmClose(false);
    setValidationError(null);
    onClose();
  };

  // Handle cancel discard
  const handleCancelDiscard = () => {
    setShowConfirmClose(false);
  };

  if (!isOpen) {
    return null;
  }

  // Show loading dialog during swap
  if (isSwapping) {
    return (
      <LoadingDialog
        isOpen={true}
        title="Swapping Meal..."
        message="Generating a completely new meal based on your instructions."
        estimatedTimeSeconds={estimatedTimeSeconds}
        isComplete={false}
      />
    );
  }

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="swap-meal-dialog-title"
      style={{
        position: 'fixed',
        top: '0px',
        left: '0px',
        width: '100vw',
        height: '100vh',
        backgroundColor: isDark ? 'rgba(0, 0, 0, 0.8)' : 'rgba(0, 0, 0, 0.5)',
        zIndex: 99999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          handleClose();
        }
      }}
    >
      <div
        style={{
          backgroundColor: isDark ? '#1f2937' : 'white',
          borderRadius: '12px',
          width: '100%',
          maxWidth: '500px',
          maxHeight: '90vh',
          overflow: 'auto',
          boxShadow: isDark
            ? '0 20px 25px -5px rgba(0, 0, 0, 0.4)'
            : '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
          border: isDark ? '1px solid #374151' : 'none',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Unsaved Changes Confirmation */}
        {showConfirmClose && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: isDark ? 'rgba(0, 0, 0, 0.8)' : 'rgba(0, 0, 0, 0.5)',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1,
            }}
          >
            <div
              style={{
                backgroundColor: isDark ? '#1f2937' : 'white',
                borderRadius: '8px',
                padding: '24px',
                maxWidth: '400px',
                margin: '20px',
                border: isDark ? '1px solid #374151' : 'none',
              }}
            >
              <h3
                style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  color: isDark ? '#f9fafb' : '#111827',
                  margin: '0 0 12px 0',
                }}
              >
                Discard Changes?
              </h3>
              <p
                style={{
                  fontSize: '14px',
                  color: isDark ? '#d1d5db' : '#6b7280',
                  margin: '0 0 20px 0',
                  lineHeight: '1.5',
                }}
              >
                You have unsaved changes. Are you sure you want to discard them?
              </p>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <Button variant="outline" onClick={handleCancelDiscard}>
                  Cancel
                </Button>
                <Button variant="secondary" onClick={handleDiscardChanges}>
                  Discard
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Header */}
        <div
          style={{
            padding: '24px',
            borderBottom: isDark ? '1px solid #374151' : '1px solid #e5e7eb',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
          }}
        >
          <div>
            <h2
              id="swap-meal-dialog-title"
              style={{
                fontSize: '20px',
                fontWeight: '600',
                color: isDark ? '#f9fafb' : '#111827',
                margin: '0 0 4px 0',
              }}
            >
              Swap Meal
            </h2>
            <p
              style={{
                fontSize: '14px',
                color: isDark ? '#9ca3af' : '#6b7280',
                margin: 0,
              }}
            >
              {meal.mealName}
            </p>
          </div>
          <CloseButton
            onClick={handleClose}
            ariaLabel="Close dialog"
          />
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div style={{ padding: '24px' }}>
            {/* Error Message */}
            {error && (
              <div
                style={{
                  padding: '12px 16px',
                  borderRadius: '8px',
                  backgroundColor: isDark ? '#7f1d1d' : '#fee2e2',
                  marginBottom: '20px',
                  display: 'flex',
                  gap: '12px',
                }}
              >
                <svg
                  style={{ width: '20px', height: '20px', color: '#ef4444', flexShrink: 0 }}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <p
                  style={{
                    fontSize: '13px',
                    color: isDark ? '#fca5a5' : '#991b1b',
                    margin: 0,
                  }}
                >
                  {error.message || 'Failed to swap meal. Please try again.'}
                </p>
              </div>
            )}

            {/* Special Instructions Input */}
            <div style={{ marginBottom: '20px' }}>
              <label
                htmlFor="instructions-input"
                style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: isDark ? '#f9fafb' : '#111827',
                  marginBottom: '8px',
                }}
              >
                Special Instructions <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <TextArea
                ref={textareaRef}
                id="instructions-input"
                value={specialInstructions}
                onChange={(e) => {
                  const newValue = e.target.value;
                  // Enforce character limit by truncating
                  if (newValue.length <= MAX_INSTRUCTIONS_LENGTH) {
                    setSpecialInstructions(newValue);
                  } else {
                    setSpecialInstructions(newValue.slice(0, MAX_INSTRUCTIONS_LENGTH));
                  }
                  setValidationError(null);
                }}
                placeholder="Describe the replacement meal"
                rows={4}
                maxLength={MAX_INSTRUCTIONS_LENGTH}
                aria-invalid={!!validationError}
                aria-describedby={validationError ? 'instructions-error' : 'instructions-help'}
                style={{
                  borderColor: validationError
                    ? '#ef4444'
                    : isDark
                      ? '#4b5563'
                      : '#d1d5db',
                }}
              />
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginTop: '6px',
                }}
              >
                {validationError ? (
                  <span
                    id="instructions-error"
                    style={{
                      fontSize: '12px',
                      color: '#ef4444',
                    }}
                  >
                    {validationError}
                  </span>
                ) : (
                  <span
                    id="instructions-help"
                    style={{
                      fontSize: '12px',
                      color: isDark ? '#9ca3af' : '#6b7280',
                    }}
                  >
                    Describe what kind of meal you want to generate
                  </span>
                )}
                <span
                  style={{
                    fontSize: '12px',
                    color:
                      specialInstructions.length > MAX_INSTRUCTIONS_LENGTH
                        ? '#ef4444'
                        : isDark
                          ? '#9ca3af'
                          : '#6b7280',
                  }}
                >
                  {specialInstructions.length}/{MAX_INSTRUCTIONS_LENGTH}
                </span>
              </div>
            </div>

            {/* Mode Selection */}
            <div style={{ marginBottom: '20px' }}>
              <ButtonGroup
                label="Generation Mode"
                options={[
                  {
                    value: 'fast',
                    label: 'Fast',
                    description: '~30 seconds',
                  },
                  {
                    value: 'quality',
                    label: 'Quality',
                    description: '~90 seconds',
                  },
                ]}
                required={true}
                maxColumns={2}
                value={mode}
                onChange={(value) => setMode(value as 'fast' | 'quality')}
                orientation="horizontal"
                size="md"
              />
            </div>

            {/* Info Box */}
            <div
              style={{
                padding: '12px 16px',
                borderRadius: '8px',
                backgroundColor: isDark ? '#7c2d12' : '#fef3c7',
                marginBottom: '20px',
                display: 'flex',
                gap: '12px',
              }}
            >
              <svg
                style={{ width: '20px', height: '20px', color: '#f59e0b', flexShrink: 0 }}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              <div>
                <p
                  style={{
                    fontSize: '12px',
                    color: isDark ? '#fcd34d' : '#b45309',
                    margin: 0,
                    lineHeight: '1.4',
                  }}
                >
                  Estimated time: ~{estimatedTimeSeconds} seconds
                </p>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div
            style={{
              padding: '16px 24px',
              borderTop: isDark ? '1px solid #374151' : '1px solid #e5e7eb',
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end',
            }}
          >
            <Button type="button" variant="ghost" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={specialInstructions.trim().length < MIN_INSTRUCTIONS_LENGTH || !!validationError}
            >
              Swap Meal
            </Button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default SwapMealDialog;